const BAR_COLOR = '#A37E5C';
const CASH_COLOR = '#A37E5C';
const NON_CASH_COLOR = '#6B8F71';

export function toLocalYMD(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function startOfMonthYMD(date = new Date()) {
  return toLocalYMD(new Date(date.getFullYear(), date.getMonth(), 1));
}

function pad(n) {
  return String(n).padStart(2, '0');
}

function formatDay(date) {
  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}`;
}

export function filterActiveOrders(orders) {
  return (orders || []).filter((order) => Number(order.is_delete) !== 1);
}

export function orderPrice(order) {
  return Number(order.price) || 0;
}

export function parseOrderDate(order) {
  if (!order?.date) return null;
  const raw = String(order.date).replace(' ', 'T');
  const date = new Date(raw);
  return Number.isNaN(date.getTime()) ? null : date;
}

function getISOWeekStart(date) {
  const start = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const day = start.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  start.setDate(start.getDate() + diff);
  return start;
}

export function aggregateRevenue(orders, groupBy) {
  const map = new Map();
  for (const order of filterActiveOrders(orders)) {
    const date = parseOrderDate(order);
    if (!date) continue;

    let key;
    let label;
    let sort;
    if (groupBy === 'week') {
      const start = getISOWeekStart(date);
      const end = new Date(start);
      end.setDate(end.getDate() + 6);
      key = `${start.getFullYear()}-${pad(start.getMonth() + 1)}-${pad(start.getDate())}`;
      label = `${formatDay(start)}–${formatDay(end)}`;
      sort = start.getTime();
    } else {
      key = `${date.getFullYear()}-${pad(date.getMonth() + 1)}`;
      label = `${pad(date.getMonth() + 1)}.${date.getFullYear()}`;
      sort = date.getFullYear() * 100 + date.getMonth();
    }

    const prev = map.get(key) || { key, label, sort, value: 0 };
    prev.value += orderPrice(order);
    map.set(key, prev);
  }
  return Array.from(map.values()).sort((a, b) => a.sort - b.sort);
}

export function aggregateTechnicianSalary(orders, technics, unknownLabel) {
  const list = Array.isArray(technics) ? technics : Object.values(technics || {});
  const names = new Map();
  list.forEach((technic) => {
    names.set(technic.id, `${technic.name || ''} ${technic.surname || ''}`.trim());
  });

  const map = new Map();
  for (const technic of list) {
    map.set(technic.id, {
      id: technic.id,
      name: names.get(technic.id) || unknownLabel,
      value: 0,
    });
  }

  for (const order of filterActiveOrders(orders)) {
    const id = order.technic_id;
    if (!id) continue;
    const name = names.get(id) || order.technic_name || unknownLabel;
    const prev = map.get(id) || { id, name, value: 0 };
    prev.value += orderPrice(order) * ((Number(order.commission) || 0) / 100);
    map.set(id, prev);
  }

  return Array.from(map.values()).sort((a, b) => b.value - a.value);
}

export function aggregateDoctorRevenue(orders, unknownLabel) {
  const map = new Map();
  for (const order of filterActiveOrders(orders)) {
    const id = order.doctor_id || 0;
    const name = order.doctor_name || unknownLabel;
    const prev = map.get(id) || { id, name, value: 0 };
    prev.value += orderPrice(order);
    map.set(id, prev);
  }
  return Array.from(map.values()).sort((a, b) => b.value - a.value);
}

export function aggregatePaymentTypes(orders, labels) {
  let cash = 0;
  let nonCash = 0;
  for (const order of filterActiveOrders(orders)) {
    const type = String(order.type_pay || '').toLowerCase();
    const amount = orderPrice(order);
    if (type === 'cash') cash += amount;
    else nonCash += amount;
  }
  return [
    { name: labels.cash, value: Math.round(cash) },
    { name: labels.nonCash, value: Math.round(nonCash) },
  ];
}

export function hasChartValues(items) {
  return (items || []).some((item) => Number(item.value) > 0);
}

export function buildBarOption({ categories, values, title, unit, rotate, horizontal }) {
  const many = categories.length > 8;
  const option = {
    title: {
      text: title,
      left: 'center',
      textStyle: { fontSize: 14, fontWeight: 600, color: '#333' },
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      valueFormatter: (value) => `${Math.round(value || 0)} ${unit}`,
    },
    grid: {
      left: 12,
      right: horizontal ? 48 : 16,
      top: 48,
      bottom: horizontal ? 32 : (many ? 64 : 32),
      containLabel: true,
    },
    series: [
      {
        type: 'bar',
        data: values.map((value) => Math.round(value)),
        itemStyle: { color: BAR_COLOR },
        barMaxWidth: 36,
        label: {
          show: values.length <= 12,
          position: horizontal ? 'right' : 'top',
          fontSize: 10,
          formatter: (params) => Math.round(params.value),
        },
      },
    ],
  };

  if (horizontal) {
    option.xAxis = {
      type: 'value',
      name: unit,
      nameTextStyle: { fontSize: 9 },
      axisLabel: {
        rotate: 90,
        fontSize: 9,
      },
    };
    option.yAxis = {
      type: 'category',
      data: categories,
      inverse: true,
      axisLabel: {
        interval: 0,
        rotate: 0,
        fontSize: 11,
      },
    };
    return option;
  }

  option.xAxis = {
    type: 'category',
    data: categories,
    axisLabel: {
      interval: 0,
      rotate: rotate ?? (many ? 35 : 0),
      fontSize: 11,
    },
  };
  option.yAxis = {
    type: 'value',
    name: unit,
    nameTextStyle: { fontSize: 11 },
  };
  if (categories.length > 12) {
    option.dataZoom = [
      { type: 'inside' },
      { type: 'slider', height: 16, bottom: 8 },
    ];
  }
  return option;
}

export function buildPieOption({ data, title, unit }) {
  return {
    title: {
      text: title,
      left: 'center',
      textStyle: { fontSize: 14, fontWeight: 600, color: '#333' },
    },
    tooltip: {
      trigger: 'item',
      formatter: (params) => `${params.name}: ${Math.round(params.value)} ${unit} (${params.percent}%)`,
    },
    legend: { bottom: 0 },
    color: [CASH_COLOR, NON_CASH_COLOR],
    series: [
      {
        type: 'pie',
        radius: ['38%', '64%'],
        center: ['50%', '48%'],
        data,
        label: {
          formatter: '{b}\n{d}%',
          fontSize: 12,
        },
      },
    ],
  };
}
